import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { checkBiometry, lockEnabled, setLockEnabled, verify } from '../../platform/biometrics';
import { isNative } from '../../platform/host';

/**
 * "Ask for a fingerprint when the application opens."
 *
 * Only in the Android application, and only on a phone that has a finger or a
 * face registered — an option that cannot be switched on is an option that only
 * raises questions.
 *
 * Turning it on asks for the finger straight away. Somebody who cannot get past
 * their own phone's prompt here would find that out tomorrow morning otherwise,
 * locked out of the warehouse.
 */
export function BiometricLock() {
  const { t } = useTranslation();
  const [offer, setOffer] = useState<boolean | null>(null);
  const [why, setWhy] = useState<string | null>(null);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [biometry, enabled] = await Promise.all([checkBiometry(), lockEnabled()]);
      if (cancelled) return;
      setOffer(biometry.available);
      setWhy(biometry.reason ?? null);
      setOn(enabled);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing in a browser: there is no phone to ask.
  if (!isNative) return null;
  if (offer === null) return null;

  // Present and unavailable, rather than absent. Hiding it made "no finger
  // registered on this phone" and "the reader could not be reached" the same
  // screen — and the second one is a fault somebody could act on.
  if (!offer) {
    return (
      <section className="card">
        <h3>{t('security.biometrics.title')}</h3>
        <p className="panel__hint">{t('security.biometrics.unavailable')}</p>
        {why && <p className="panel__hint">{why}</p>}
      </section>
    );
  }

  const toggle = async () => {
    setRefused(false);
    setBusy(true);
    try {
      if (!on) {
        const passed = await verify(t('security.biometrics.reason'));
        if (!passed) {
          setRefused(true);
          return;
        }
      }
      await setLockEnabled(!on);
      setOn(!on);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h3>{t('security.biometrics.title')}</h3>
      <p className="panel__hint">{t('security.biometrics.hint')}</p>
      {refused && <p className="panel__error">{t('security.biometrics.refused')}</p>}
      <button type="button" onClick={() => void toggle()} disabled={busy}>
        {t(on ? 'security.biometrics.turnOff' : 'security.biometrics.turnOn')}
      </button>
    </section>
  );
}
